import React, { useState, useEffect } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Container,
  CssBaseline,
  useTheme,
  Button,
  Tooltip,
  ThemeProvider,
  Paper,
  Badge,
  Avatar,
  Fade,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Security as SecurityIcon,
  Category as CategoryIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  Home as HomeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AdminPanelSettings as AdminIcon,
  Assignment as ListingIcon,
  History as HistoryIcon,
  Assessment as AssessmentIcon,
  List as AllListingsIcon,
  GroupWork as ManageIcon,
  Search as SearchIcon,
  BarChart as StatsIcon,
  Email as EmailIcon,
  SelectAll as BulkIcon,
  Analytics as MonitoringIcon,
  AccountBalance as PayoutsIcon,
  AttachMoney as AttachMoneyIcon,
  RateReview as ReviewIcon,
  Gavel as GavelIcon,
  AttachMoney as DeductionIcon,
  Block as BlockIcon,
  SupportAgent as SupportAgentIcon,
  Tune as TuneIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation, Link, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDashboard } from '../context/DashboardContext';
import themeManager from '../theme/themeManager';
import PERMISSIONS from '../config/permissionsList';

// Style pour la barre d'application
const AppBarStyled = styled(AppBar)(({ theme }) => ({
  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)',
  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  position: 'sticky',
  top: 0,
  zIndex: theme.zIndex.drawer + 1
}));

// Style pour le conteneur principal
const ContentContainer = styled(Container)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  width: '100%',
  maxWidth: '100% !important' // Forcer la largeur maximale
}));

// Style pour le footer
const Footer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: 'center',
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
  width: '100%',
  position: 'sticky',
  bottom: 0,
  zIndex: 1
}));

// Style pour les éléments du menu
const StyledMenuItem = styled(MenuItem)(({ theme, isActive }) => ({
  borderRadius: theme.shape.borderRadius,
  margin: theme.spacing(0.5, 0),
  transition: 'all 0.2s ease-in-out',
  backgroundColor: isActive ? theme.palette.action.selected : 'transparent',
  '&:hover': {
    backgroundColor: isActive
        ? theme.palette.action.selected
        : theme.palette.action.hover,
    transform: 'translateX(5px)',
  },
}));

// Style pour le conteneur principal
const MainContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  width: '100%'
}));

/**
 * Layout partagé pour toutes les pages d'administration
 * Fournit un header fixe avec menu de navigation
 */
const AdminLayout = () => {
  const theme = useTheme();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const { user, logout } = useAuth();
  const { preferences } = useDashboard();
  const navigate = useNavigate();
  const location = useLocation();

  // Créer un thème personnalisé basé sur les préférences
  const customTheme = themeManager.createCustomTheme(preferences) || theme;

  /**
   * Ouvrir le menu de navigation
   */
  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  /**
   * Fermer le menu de navigation
   */
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  /**
   * Déconnecter l'utilisateur
   */
  const handleLogout = () => {
    logout();
    navigate('/login');
    handleMenuClose();
  };

  /**
   * Naviguer vers une page et fermer le menu
   */
  const handleNavigation = (path) => {
    navigate(path);
    handleMenuClose();
  };

  // Éléments du menu de navigation - IDENTIQUE à celui du AdminDashboard
  const menuItems = [
    { text: 'Tableau de bord', icon: <DashboardIcon />, path: '/admin', permission: PERMISSIONS.ACCESS_ADMIN_PANEL },
    { text: 'Utilisateurs', icon: <PeopleIcon />, path: '/admin/users', permission: PERMISSIONS.VIEW_USERS },
    { text: 'Rôles', icon: <SecurityIcon />, path: '/admin/roles', permission: PERMISSIONS.MANAGE_ROLES  },
    { text: 'Permissions', icon: <SecurityIcon />, path: '/admin/permissions', permission: PERMISSIONS.MANAGE_ROLES  },
    { text: '🚫 Restrictions', icon: <BlockIcon />, path: '/admin/restrictions', permission: PERMISSIONS.VIEW_RESTRICTIONS },
    { text: '📋 Appels de Ban', icon: <GavelIcon />, path: '/admin/appeals', permission: PERMISSIONS.VIEW_RESTRICTIONS },
    { text: 'Catégories', icon: <CategoryIcon />, path: '/admin/categories', permission: PERMISSIONS.VIEW_CATEGORIES  },
    { text: '🌐 Traductions Catégories', icon: <CategoryIcon />, path: '/admin/category-translations', permission: PERMISSIONS.VIEW_CATEGORIES  },
    { text: '📋 Champs de Détails', icon: <CategoryIcon />, path: '/admin/listing-fields', permission: PERMISSIONS.MANAGE_CATEGORIES  },
    
    // PHASE 5: GESTION COMPLÈTE ADMIN
    { text: '🎯 Centre Réservations', icon: <AssessmentIcon />, path: '/admin/reservations', permission: PERMISSIONS.MANAGE_RESERVATIONS },
    { text: '💰 Gestion Financière', icon: <MonitoringIcon />, path: '/admin/finances', permission: PERMISSIONS.MANAGE_PAYMENTS },
    { text: '💳 Gestion Payins', icon: <AttachMoneyIcon />, path: '/admin/payins', permission: PERMISSIONS.MANAGE_PAYMENTS },
    { text: '🏦 Gestion Payouts', icon: <PayoutsIcon />, path: '/admin/payouts', permission: PERMISSIONS.MANAGE_PAYMENTS },
    { text: '⚖️ Gestion Litiges', icon: <GavelIcon />, path: '/admin/disputes', permission: PERMISSIONS.MANAGE_DISPUTES },
    { text: '� Ponctions Pénalités', icon: <DeductionIcon />, path: '/admin/payout-deductions', permission: PERMISSIONS.MANAGE_PAYMENTS },
    { text: '�📊 Analytics & Reporting', icon: <StatsIcon />, path: '/admin/analytics', permission: PERMISSIONS.ACCESS_ADMIN_PANEL },
    
    { text: 'Toutes les Annonces', icon: <AllListingsIcon />, path: '/admin/all-listings', permission: PERMISSIONS.VIEW_PENDING_LISTINGS },
    { text: 'Validation Annonces', icon: <ListingIcon />, path: '/admin/listings', permission: PERMISSIONS.VIEW_PENDING_LISTINGS },
    { text: 'Historique Validations', icon: <HistoryIcon />, path: '/admin/listings/history', permission: PERMISSIONS.VIEW_PENDING_LISTINGS },
    { text: 'Statistiques Validations', icon: <AssessmentIcon />, path: '/admin/listings/stats', permission: PERMISSIONS.VIEW_PENDING_LISTINGS },
    { text: 'Notifications', icon: <NotificationsIcon />, path: '/admin/notifications', permission: PERMISSIONS.MANAGE_NOTIFICATION },
    { text: '📊 Dashboard Monitoring', icon: <MonitoringIcon />, path: '/admin/monitoring', permission: PERMISSIONS.ACCESS_ADMIN_PANEL },
    { text: '🗺️ Monitoring Géocodage', icon: <MonitoringIcon />, path: '/admin/geocoding-monitoring', permission: PERMISSIONS.ACCESS_ADMIN_PANEL },
    
    // Section User ↔ Listings
    { text: 'Relations User ↔ Listings', icon: <ManageIcon />, path: '/admin/user-listings', permission: PERMISSIONS.MANAGE_USER_LISTING },
    { text: '🤖 Jobs Automatisés', icon: <AssessmentIcon />, path: '/admin/automated-jobs', permission: PERMISSIONS.VIEW_AUTOMATED_JOBS },
  { text: '🛡️ Reviews Locataire → Proprio', icon: <ReviewIcon />, path: '/admin/reviews/moderation', permission: PERMISSIONS.MANAGE_REVIEWS },
  { text: '🛡️ Reviews Proprio → Locataire', icon: <ReviewIcon />, path: '/admin/reviews/owner-renter-moderation', permission: PERMISSIONS.MANAGE_REVIEWS },
  { text: '🛡️ Reviews Réponses', icon: <ReviewIcon />, path: '/admin/reviews/response-moderation', permission: PERMISSIONS.MANAGE_REVIEWS },
  { text: 'Recherche Avancée', icon: <SearchIcon />, path: '/admin/user-listings/search', permission: PERMISSIONS.MANAGE_USER_LISTING },
    { text: 'Statistiques Relations', icon: <StatsIcon />, path: '/admin/user-listings/stats', permission: PERMISSIONS.VIEW_USER_LISTING_STATS },
    { text: 'Actions Groupées', icon: <BulkIcon />, path: '/admin/user-listings/bulk-actions', permission: PERMISSIONS.BULK_USER_LISTING_ACTIONS },
    { text: 'Communication Email', icon: <EmailIcon />, path: '/admin/user-listings/email', permission: PERMISSIONS.SEND_USER_LISTING_EMAIL },
    
    { text: 'Paramètres', icon: <SettingsIcon />, path: '/admin/settings', permission: PERMISSIONS.MANAGE_SYSTEM },    { text: '⚙️ Config Système', icon: <TuneIcon />, path: '/admin/config', permission: PERMISSIONS.MANAGE_SYSTEM },    { text: '🎧 Support & Aide', icon: <SupportAgentIcon />, path: '/admin/support', permission: PERMISSIONS.ACCESS_ADMIN_PANEL },
  ];

  // Obtenir les initiales de l'utilisateur pour l'avatar
  const getUserInitials = () => {
    if (!user?.name) return '?';
    return user.name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  return (
    <ThemeProvider theme={customTheme}>
      <CssBaseline />
      <MainContainer>
        <AppBarStyled>
          <Toolbar>
            {/* Menu de navigation */}
            <Box sx={{ position: 'relative' }}>
              <Button
                color="inherit"
                startIcon={<MenuIcon />}
                endIcon={menuAnchorEl ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={handleMenuOpen}
                aria-controls={menuAnchorEl ? 'admin-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={menuAnchorEl ? 'true' : undefined}
                sx={{
                  mr: 2,
                  backgroundColor: menuAnchorEl ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)',
                  borderRadius: '20px',
                  px: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.2)'
                  }
                }}
              >
                Menu Admin
              </Button>

              <Menu
                id="admin-menu"
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                MenuListProps={{
                  'aria-labelledby': 'admin-menu-button',
                }}
                PaperProps={{
                  elevation: 3,
                  sx: {
                    width: 250,
                    maxHeight: 'calc(100vh - 100px)',
                    overflow: 'auto',
                    mt: 1,
                    '& .MuiList-root': {
                      py: 1,
                      px: 1
                    }
                  }
                }}
                TransitionComponent={Fade}
                TransitionProps={{ timeout: 200 }}
              >
                <Box sx={{ p: 2, pb: 1 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: theme.palette.background.default,
                      borderRadius: 2,
                      mb: 1
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        mr: 2
                      }}
                    >
                      {getUserInitials()}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {user?.name || 'Utilisateur'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Administrateur
                      </Typography>
                    </Box>
                  </Paper>
                </Box>

                <Divider sx={{ mx: 1, mb: 1 }} />

                {menuItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <StyledMenuItem
                      key={item.text}
                      onClick={() => handleNavigation(item.path)}
                      isActive={isActive}
                    >
                      <ListItemIcon sx={{
                        color: isActive ? theme.palette.primary.main : 'inherit',
                        minWidth: '40px'
                      }}>
                        {item.text === 'Notifications' ? (
                          <Badge color="error" variant="dot">
                            {item.icon}
                          </Badge>
                        ) : item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontWeight: isActive ? 'bold' : 'normal',
                          color: isActive ? theme.palette.primary.main : 'inherit'
                        }}
                      />
                    </StyledMenuItem>
                  );
                })}

                <Divider sx={{ mx: 1, my: 1 }} />

                <StyledMenuItem onClick={() => handleNavigation('/agent-dashboard')}>
                  <ListItemIcon sx={{ minWidth: '40px' }}>
                    <SupportAgentIcon />
                  </ListItemIcon>
                  <ListItemText primary="Dashboard Agent" />
                </StyledMenuItem>

                <StyledMenuItem onClick={handleLogout}>
                  <ListItemIcon sx={{ minWidth: '40px' }}>
                    <LogoutIcon />
                  </ListItemIcon>
                  <ListItemText primary="Déconnexion" />
                </StyledMenuItem>
              </Menu>
            </Box>

            <Typography variant="h6" component="div" sx={{
              flexGrow: 1,
              fontWeight: 'bold',
              letterSpacing: '0.5px',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Box component="span" sx={{
                mr: 1,
                backgroundColor: 'rgba(255,255,255,0.2)',
                p: 0.5,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center'
              }}>
                <AdminIcon />
              </Box>
              Tableau de bord d'administration
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                component={Link}
                to="/dashboard"
                color="inherit"
                startIcon={<HomeIcon />}
                sx={{
                  mr: 2,
                  borderRadius: '20px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.2)'
                  }
                }}
              >
                Site
              </Button>

              <Button
                component={Link}
                to="/agent-dashboard"
                color="inherit"
                startIcon={<SupportAgentIcon />}
                sx={{
                  mr: 2,
                  borderRadius: '20px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.2)'
                  }
                }}
              >
                Agent
              </Button>

              <Tooltip title={user?.name || 'Utilisateur'}>
                <Avatar
                  sx={{
                    bgcolor: theme.palette.secondary.main,
                    color: theme.palette.secondary.contrastText,
                    mr: 1,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                  }}
                >
                  {getUserInitials()}
                </Avatar>
              </Tooltip>

              <Tooltip title="Déconnexion">
                <IconButton
                  color="inherit"
                  onClick={handleLogout}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.2)'
                    }
                  }}
                >
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Toolbar>
        </AppBarStyled>

        <ContentContainer>
          <Fade in={true} timeout={500}>
            {/* Le contenu spécifique à chaque page sera rendu ici */}
            <Box>
              <Outlet />
            </Box>
          </Fade>
        </ContentContainer>

        <Footer>
          <Typography variant="body2" color="textSecondary">
            © {new Date().getFullYear()} Krillou Admin - Tous droits réservés
          </Typography>
        </Footer>
      </MainContainer>
    </ThemeProvider>
  );
};

export default AdminLayout;
